/**
 * storageService.ts
 * Generic Firebase Storage utilities shared across all upload flows.
 * Entity-specific services (bankerService, taskService, etc.) delegate here.
 */
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import type { DocumentAttachment } from '@/types';

/**
 * Upload a single file to Firebase Storage at the given path.
 * Returns a DocumentAttachment with the download URL populated.
 */
export const uploadFileToStorage = async (
  file: File,
  storagePath: string
): Promise<DocumentAttachment> => {
  const storageRef = ref(storage, storagePath);
  const uploadResult = await uploadBytes(storageRef, file);
  const url = await getDownloadURL(uploadResult.ref);

  return {
    id: `doc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}_${file.name}`,
    name: file.name,
    type: file.type,
    size: file.size,
    url,
    uploadedAt: new Date(),
  };
};

/**
 * Delete a file from Firebase Storage using its download URL.
 * Silently ignores base64 data: URLs (legacy).
 */
export const deleteFileFromStorage = async (fileUrl: string): Promise<void> => {
  if (!fileUrl || fileUrl.startsWith('data:')) return;

  try {
    const url = new URL(fileUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)$/);

    if (!pathMatch) {
      console.warn('[storageService] Could not extract path from URL:', fileUrl);
      return;
    }

    const storagePath = decodeURIComponent(pathMatch[1]);
    await deleteObject(ref(storage, storagePath));
  } catch (error) {
    console.error('[storageService] Error deleting file:', error);
    // Non-fatal: don't block the caller
  }
};

/**
 * Generate a timestamped, unique filename to avoid overwriting.
 */
export const uniqueFilename = (file: File): string =>
  `${Date.now()}-${file.name}`;
